import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Carousel header arrows custom element that connects to a slideshow-component
 * and provides navigation controls with disabled state styling.
 *
 * @typedef {any} Refs
 *
 * @typedef {HTMLElement & {
 *   slides?: HTMLElement[];
 *   current?: number;
 *   infinite?: boolean;
 *   visibleSlides?: HTMLElement[];
 *   previous?: (event?: Event) => void;
 *   next?: (event?: Event) => void;
 * }} SlideshowElement
 * @extends {Component<Refs>}
 */
export class CarouselHeaderArrows extends Component {
  connectedCallback() {
    super.connectedCallback();

    const slideshowRef = this.dataset.slideshowRef;
    if (!slideshowRef) return;

    this.#previousButton = this.querySelector('.carousel-header-arrow--previous');
    this.#nextButton = this.querySelector('.carousel-header-arrow--next');

    if (!this.#previousButton || !this.#nextButton) return;

    this.#findSlideshow(slideshowRef);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#slideshow) {
      this.#slideshow.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    }

    this.#previousButton?.removeEventListener('click', this.#handlePreviousClick);
    this.#nextButton?.removeEventListener('click', this.#handleNextClick);
  }

  /** @type {HTMLButtonElement | null} */
  #previousButton = null;

  /** @type {HTMLButtonElement | null} */
  #nextButton = null;

  /** @type {SlideshowElement | null} */
  #slideshow = null;

  /**
   * Find the slideshow component by ref attribute
   * @param {string} ref - The ref attribute value to search for
   */
  #findSlideshow(ref) {
    const findAndConnect = () => {
      const selector = `slideshow-component[ref="${ref}"]`;
      // Prefer scoping to the same section/container to avoid ref collisions
      // when multiple carousels reuse the same ref (e.g. "multicolumn").
      const scope =
        this.closest('section') ||
        this.closest('.section') ||
        this.closest('.shopify-section') ||
        document;

      /** @type {SlideshowElement | null} */
      const slideshow = scope.querySelector(selector) || document.querySelector(selector);

      if (slideshow) {
        this.#slideshow = slideshow;
        this.#setupEventListeners();
        this.#updateButtonStates();
      } else {
        requestAnimationFrame(findAndConnect);
      }
    };

    findAndConnect();
  }

  #setupEventListeners() {
    if (!this.#slideshow) return;

    this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.#previousButton?.addEventListener('click', this.#handlePreviousClick);
    this.#nextButton?.addEventListener('click', this.#handleNextClick);

    const resizeObserver = new ResizeObserver(() => {
      this.#updateButtonStates();
    });
    resizeObserver.observe(this.#slideshow);
  }

  #handleSlideshowSelect = () => {
    this.#updateButtonStates();
  };

  /** @param {Event} event */
  #handlePreviousClick = (event) => {
    event.preventDefault();
    if (this.#slideshow && typeof this.#slideshow.previous === 'function') {
      this.#slideshow.previous(event);
    }
  };

  /** @param {Event} event */
  #handleNextClick = (event) => {
    event.preventDefault();
    if (this.#slideshow && typeof this.#slideshow.next === 'function') {
      this.#slideshow.next(event);
    }
  };

  #updateButtonStates() {
    if (!this.#slideshow || !this.#previousButton || !this.#nextButton) return;

    const { current, slides, infinite, visibleSlides } = this.#slideshow;
    const slideCount = slides?.length || 0;

    if (slideCount === 0) {
      return;
    }

    if (infinite) {
      this.#previousButton.disabled = false;
      this.#nextButton.disabled = false;
      return;
    }

    const visibleCount = visibleSlides?.length || 1;
    const currentIndex = current ?? 0;
    const nextIndex = currentIndex + visibleCount;

    this.#previousButton.disabled = currentIndex === 0;
    this.#nextButton.disabled = nextIndex >= slideCount;
  }
}

if (!customElements.get('carousel-header-arrows')) {
  customElements.define('carousel-header-arrows', CarouselHeaderArrows);
}
